/**
 * Words for an answer's context: how a turn relates to the previous one, and
 * what changed. Pure, so the strip stays a thin rendering of these.
 */
import { memberLabel } from "../../../services/chartOptions";
import type {
  AnswerChange,
  ChangeField,
  TurnRelation,
} from "../../../services/answerContext";

const RELATION_LABELS: Record<TurnRelation, string> = {
  new: "New question",
  refine: "Refined",
  drill: "Drilled down",
  compare: "Compared",
  rechart: "Redrawn",
  expand: "Extended",
  explain: "Explained",
};

const FIELD_LABELS: Record<ChangeField, string> = {
  metrics: "Measures",
  dimensions: "Breakdown",
  filters: "Filters",
  period: "Period",
  chart: "Chart",
  limit: "Limit",
};

// A certified member at the start of a value: "dim_company.region" alone, or
// leading a filter such as "dim_company.region equals EMEA".
const LEADING_MEMBER = /^([A-Za-z][\w]*\.[A-Za-z][\w]*)(?=$|\s)/;

export function relationLabel(relation: TurnRelation): string {
  return RELATION_LABELS[relation];
}

/** A value as a reader says it: members by business title, phrases as given. */
export function readableValue(value: string): string {
  return value.replace(LEADING_MEMBER, (member) => memberLabel(member));
}

function list(values: string[]): string {
  return values.map(readableValue).join(", ");
}

/** One change as a sentence fragment, such as "Breakdown: Account Type → Company Name". */
export function describeChange(change: AnswerChange): string {
  const label = FIELD_LABELS[change.field];
  if (!change.before.length && change.after.length) {
    return `${label}: added ${list(change.after)}`;
  }
  if (change.before.length && !change.after.length) {
    return `${label}: removed ${list(change.before)}`;
  }
  return `${label}: ${list(change.before)} → ${list(change.after)}`;
}
